/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { ReactComponent as BinSvg } from 'assets/Icons/bin.svg';
import { ReactComponent as AddSvg } from 'assets/Icons/plus_icon.svg';
import {
  CustomSelectSearch,
  Option,
} from 'components/shared/CustomSelectSearch';
import { Hint } from 'components/shared/Hint';
import { findIndex, isArray, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  addExcludeIntegrationFilter,
  removeExcludeIntegrationFilter,
  updateSingleIntegration,
} from 'slices/omny.slice';
import { IntegrationOptions, ProgramIdOption } from 'utils/models';
import { customToast } from 'utils/toast.util';

import { defaultStyles } from './IntegrationFilter';

type Props = {
  integration: IntegrationOptions;
  programIds: ProgramIdOption[];
  isLoading?: boolean;
};

const defaultExcludeRow = { programId: '', programName: '' };

export const IntegrationExcludeProgramFilter = (props: Props) => {
  const { integration, programIds, isLoading = false } = props;

  const dispatch = useDispatch();

  const [listProgramIdsDropdown, setListProgramIdsDropdown] =
    useState<ProgramIdOption[]>();

  const excludeProgramFilters = integration.filter.excludeProgramFilters ?? [
    defaultExcludeRow,
  ];

  useEffect(() => {
    if (isEmpty(programIds) && isArray(programIds)) {
      setListProgramIdsDropdown([]);
      return;
    }

    setListProgramIdsDropdown(programIds);
  }, [programIds]);

  const handleSelectProgramId = (newValue: Option, index: number) => {
    if (isEmpty(newValue.value) && isEmpty(newValue.label)) return;

    const newIntegration = { ...integration };
    const newExcludeFilters = [
      ...(newIntegration.filter.excludeProgramFilters ?? [defaultExcludeRow]),
    ];

    const foundDuplicate = findIndex(
      newExcludeFilters,
      (filter) => filter.programId === newValue.value,
    );
    if (foundDuplicate >= 0 && foundDuplicate !== index) {
      customToast.error('Program ID already in exclude list');
      return;
    }

    const row = { ...newExcludeFilters[index] };
    row.programId = newValue.value;
    row.programName = newValue.label;
    newExcludeFilters[index] = row;

    dispatch(
      updateSingleIntegration({
        ...newIntegration,
        filter: {
          ...integration.filter,
          excludeProgramFilters: newExcludeFilters,
        },
      }),
    );
  };

  const renderOptionLabel = (option: ProgramIdOption) => (
    <div tw="flex flex-col w-full py-0.5">
      <div
        tw="text-15 font-black text-left text-sonnant-dark break-words"
        className="line-clamp-2"
      >
        {option.label}
      </div>
      <div tw="text-12 text-sonnant-grey-6">{option.value}</div>
    </div>
  );

  const handleAddRow = () => {
    dispatch(addExcludeIntegrationFilter(integration));
  };

  const handleRemoveRow = (index: number) => {
    dispatch(
      removeExcludeIntegrationFilter({
        integration,
        index,
      }),
    );
  };

  return (
    <div tw="sm-up:w-[65%] 2xl-up:w-1/2 mt-[1.5rem] flex flex-col pl-[3rem] gap-y-[1rem]">
      <div tw="flex flex-col gap-y-[1rem] w-full overflow-y-auto overflow-x-hidden max-height[40rem]">
        {excludeProgramFilters.map((filter, index) => (
          <div key={index} tw="flex space-x-8">
            <div tw="flex flex-col pt-0! flex-1 min-w-[27rem]">
              <div tw="flex flex-row items-center">
                <label tw="flex flex-nowrap text-16 mr-3">Program ID</label>
              </div>

              <div tw="flex flex-row max-width[75rem]">
                <div tw="flex-1 relative w-full">
                  <CustomSelectSearch
                    menuPortal
                    options={listProgramIdsDropdown ?? []}
                    defaultValue={filter.programId}
                    isLoading={isLoading}
                    customStyles={defaultStyles}
                    formatOptionLabel={renderOptionLabel}
                    onChange={(newValue: Option) =>
                      handleSelectProgramId(newValue, index)
                    }
                    menuPlacement="top"
                    canCreate={false}
                  />
                </div>
              </div>
            </div>

            <div tw="flex flex-col pt-0! flex-1 min-width[20rem]">
              <div tw="flex flex-row items-center">
                <label tw="flex flex-nowrap text-16 mr-3">Program name</label>
              </div>

              <div tw="flex flex-row max-width[75rem]">
                <div tw="flex-1 relative ">
                  <input
                    tw="flex"
                    type="text"
                    name="excludeProgramName"
                    disabled
                    value={filter.programName}
                  />
                </div>
              </div>
            </div>

            <div tw="flex flex-1 space-x-5">
              {index === excludeProgramFilters.length - 1 && (
                <Hint
                  text="Add excluded program"
                  enterDelay={100}
                  leaveDelay={0}
                >
                  <button
                    type="button"
                    onClick={handleAddRow}
                    tw="flex justify-center self-end mb-[1rem] pt-0! items-center cursor-pointer rounded-lg h-[4rem] w-[4rem]"
                    className="btn btn-secondary"
                  >
                    <AddSvg tw="w-[3rem]" />
                  </button>
                </Hint>
              )}
              <Hint
                text="Remove excluded program"
                enterDelay={100}
                leaveDelay={0}
              >
                <button
                  type="button"
                  onClick={() => handleRemoveRow(index)}
                  tw="flex justify-center self-end mb-[1rem] pt-0! items-center cursor-pointer h-[4rem] w-[4rem]"
                  className="btn btn-secondary"
                >
                  <BinSvg fill="red" />
                </button>
              </Hint>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
